import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import VisitIcon from "../../resources/icons/external-link.svg";
import Toast from "../../common/Toast/Toast";
import EvidenceModal from "../../common/EvidenceModal/EvidenceModal";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import CfLoader from "../../common/CfLoader/CfLoader";
import ImagePreviewModal from "../../common/ImagePreviewModal/ImagePreviewModal";
import DownloadIcon from "../../resources/icons/download-01.svg";
import renderPdf from "../../common/RenderPdf/RenderPdf";

const CredentialPreview = () => {
  const { cert_hash } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarksheets, setShowMarksheets] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [isPdfLoading] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const pdfCanvasRef = useRef(null);

  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const fetchCertificateData = async () => {
    try {
      const response = await GET(
        `${EntityConstants.GET_CERTIFICATE_PREVIEW}?encoded_data=${cert_hash}&format=json`
      );
      const responseData = await response.json();
      if (response.ok) {
        setCertificateData(responseData);
        if (
          responseData.pdf_url &&
          (responseData.pdf_url.endsWith(".pdf") ||
            responseData.pdf_url.includes(".pdf?"))
        ) {
          setIsPdf(true);
          renderPdf(responseData.pdf_url, pdfCanvasRef);
        }
      } else {
        console.log(
          responseData?.error || responseData?.message || responseData?.detail
        );
        setError("Something went wrong. Please try again!");
        const errorMessage =
          responseData?.error ||
          responseData?.message ||
          responseData?.detail ||
          "An unknown error occurred";
        Toast({
          type: "error",
          title: "Error",
          message: errorMessage,
        });
      }
    } catch (err) {
      setError("Something went wrong. Please try again!");
      Toast({
        type: "error",
        title: "Error",
        message: err?.message || "Failed to load certificate data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (certificateData?.download_presentation) {
      try {
        const response = await fetch(certificateData.download_presentation);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = certificateData.download_presentation_filename;      
        document.body.appendChild(link);

        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to download certificate",
        });

        const fallbackLink = document.createElement("a");
        fallbackLink.href = certificateData.download_presentation;
        fallbackLink.target = "_blank";
        fallbackLink.download = certificateData.download_presentation_filename;
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
      }
    } else {
      Toast({
        type: "error",
        title: "Error",
        message: "Download URL not available",
      });
    }
  };

  useEffect(() => {
    fetchCertificateData();
  }, [cert_hash]);

  const handleRedirect = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleKeyDown = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div
            className="spinner-border animate-spin inline-block w-12 h-12 border-4 border-t-4 border-gray-200 rounded-full"
            role="status"
          ></div>
          <div className="mt-4 text-lg text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-red-600 text-center p-4">{error}</div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-6 min-h-screen flex flex-col justify-start font-sans text-gray-900 bg-white overflow-x-hidden">
      {/* Marksheets Modal */}
      <EvidenceModal
        isOpen={showMarksheets}
        toggleModal={setShowMarksheets}
        marksheets={certificateData.evidence || []}
      />
      <ImagePreviewModal
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        imageUrl={certificateData.svg_string}
        downloadUrl={certificateData.download_presentation}
        credentialId={certificateData.credential_id}
      />
      <header className="pb-2 mb-6">
        <img
          src={certificateData.issuer_logo}
          alt="Issuer Logo"
          className="h-10 mb-3 mt-3"
        />
        <hr className="full-width my-2" />
        <h2 className="text-2xl mt-4">{certificateData.certificate_name}</h2>
      </header>

      <div className="w-full flex flex-col md:flex-row gap-4">
        <div className="flex flex-col items-stretch w-full lg:w-1/3 gap-4">
          <div className="w-full flex flex-col py-4 px-4 border border-gray-300 rounded-lg relative lg:mb-0 justify-between order-1">
            <p className="text-l text-gray-600 mb-2">Certificate issued to</p>
            <h3 className="text-lg font-bold mb-4">
              {certificateData.recipient_name}
            </h3>
            <p className="text-l text-gray-600 mb-2">Unique ID</p>
            <h3 className="text-lg font-bold mb-4">
              {certificateData.credential_id}
            </h3>
            <p className="text-l text-gray-600 mb-2">Issuance Date</p>
            <h3 className="text-lg font-bold mb-4">
              {certificateData.issued_at}
            </h3>

            {certificateData.evidence &&
              certificateData.evidence.filter((item) => item.evidence_url)
                .length > 0 && (
                <button
                  onClick={() => setShowMarksheets(true)}
                  className="px-2 h-[2.75rem] bg-[#3538CD] text-white rounded-[8px] font-medium flex items-center justify-center space-x-2 hover:bg-cfPrimaryDark transition duration-200 w-full border border-gray-800"
                >
                  <span>View Certificates</span>
                </button>
              )}
          </div>

          <div className="w-full flex flex-col rounded-lg relative lg:mb-0 justify-between order-2">
            <div className="p-4 border border-gray-300 rounded-lg flex flex-col">
              <h4 className="font-bold mb-4">Issued By</h4>
              <div
                className={`flex items-center ${
                  certificateData.course_url ? "mb-4" : "mb-0"
                } space-x-3`}
              >
                <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-300">
                  <img
                    src={certificateData.issuer_logo}
                    alt="Issuer Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p>{certificateData.issued_by}</p>
              </div>

              {certificateData.course_url && (
                <button
                  onClick={() => handleRedirect(certificateData.course_url)}
                  className="bg-gray-50 text-gray-900 py-2 px-3 text-sm rounded hover:bg-gray-300 transition duration-200 w-full border border-gray-800 flex items-center justify-center space-x-1"
                >
                  <span>{`Visit ${certificateData.issued_by}`}</span>
                  <img
                    src={VisitIcon}
                    alt="Icon"
                    className="h-4 w-4 text-gray-800"
                  />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-2/3 border border-gray-300 rounded-lg mb-4 lg:mb-0 flex flex-col justify-between order-1 lg:order-2 relative group">
          <div
            className={`w-full ${
              certificateData.course_url ? "h-[35.4rem]" : "h-[28.6rem]"
            }  bg-gray-100 text-center border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center`}
          >
            {isPdf ? (
              isPdfLoading ? (
                <div className="flex items-center justify-center w-full h-full">
                  <CfLoader />
                </div>
              ) : (
                <canvas ref={pdfCanvasRef} className="max-w-full max-h-full" />
              )
            ) : (
              <>
                <img
                  src={certificateData.svg_string}
                  alt="Certificate Preview"
                  className="object-contain w-full h-full"
                />
                <button
                  className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer focus:outline-none"
                  onClick={() => setShowImagePreview(true)}
                  onKeyDown={(e) => handleKeyDown(e, () => setShowImagePreview(true))}
                  aria-label="Preview certificate in full screen"
                  type="button"
                >
                  <div className="flex flex-col items-center">
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <div className="bg-white bg-opacity-80 rounded-full p-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-gray-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                          />
                        </svg>
                      </div>
                    </div>
                    <span className="text-white font-medium">Preview</span>
                  </div>
                </button>
                <button
                  className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md cursor-pointer focus:outline-none"
                  onClick={handleDownload}
                  onKeyDown={(e) => handleKeyDown(e, handleDownload)}
                  aria-label="Download certificate"
                  type="button"
                >
                  <img src={DownloadIcon} className="h-5 w-5 z-10" alt="Download" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialPreview;
